/**
 * YUMIX — Offline Mode status hook + pill banner.
 *
 * Detects network state via the Navigator API + a lightweight heartbeat
 * to /api/health. When offline, exposes `isOffline=true` so pages can:
 *   • Serve data from localStorage SWR snapshot only (no live fetch)
 *   • Show a subtle top-bar pill so the user is aware
 *   • Queue write operations (future work: IndexedDB outbox)
 *
 * Keeps the footprint microscopic — no new deps, no SW, no PWA wiring
 * (those require separate build changes). This is phase 1.
 */
import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

export function useOfflineStatus() {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return isOffline;
}

export default function OfflineIndicator({ language = 'fr' }) {
  const isOffline = useOfflineStatus();
  if (!isOffline) return null;
  const fr = language === 'fr';
  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] flex justify-center pointer-events-none"
      data-testid="offline-indicator"
    >
      <div className="mt-2 px-3 py-1.5 rounded-full bg-amber-500/90 text-amber-950 text-xs font-semibold shadow-lg flex items-center gap-2 pointer-events-auto">
        <WifiOff className="w-3.5 h-3.5" />
        {fr ? 'Hors-ligne — mode lecture seule' : 'Offline — read-only mode'}
      </div>
    </div>
  );
}
